// @ts-nocheck
import get from "lodash.get";
import merge from "lodash.merge";
import { createIntl, createIntlCache } from "react-intl";

import moment from "moment";
import frData from "./locales/fr";
import * as overrides from "./overrides";
import "moment-timezone";
import "moment/locale/fr";

class IntlUtils {
  LOCALES = ["fr"];

  DEFAULT_LOCALE = "fr";

  intlInstance = null;

  LOCALE = "fr";

  localeData = {};

  initialize = (currentLocale, intlOverrides: string[]) => {
    // use to import only the files used by the current locale
    return this.getLocaleData(currentLocale, intlOverrides).then(
      (localeData) => {
        this.localeData = {
          ...this.localeData,
          ...localeData,
        };

        this.setLocale({ locale: currentLocale });

        return this.localeData;
      }
    );
  };

  // For file import, the locale needs to be normalized to "en" or "fr"
  // because sometimes the env variable could be "en_GB", "fr_BE" ...
  getNormalizedLocale = (locale = "fr") => {
    if (locale.toLowerCase().startsWith("fr")) {
      return "fr";
    }
    if (locale.toLowerCase().startsWith("en")) {
      return "en";
    }

    return locale;
  };

  getLocaleData = async (locale = "fr", intlOverrides: string[] = []) => {
    let data = merge({}, frData);
    intlOverrides.forEach((override) => {
      const keyOverride = `${override}${locale.toLocaleUpperCase()}`;
      if (overrides[keyOverride]) {
        data = merge(data, overrides[keyOverride]);
      }
    });
    return this.getFlattenDataFromFile(data);
  };

  getFlattenDataFromFile = (traductionFile = {}) => ({
    [traductionFile?.locale]: {
      ...traductionFile,
      messages: this.flattenMessages(traductionFile.messages),
    },
  });

  // use this to allow nested messages, taken from docs:
  // https://github.com/yahoo/react-intl/wiki/Upgrade-Guide#flatten-messages-object
  flattenMessages = (nestedMessages = {}, prefix = "") =>
    Object.keys(nestedMessages).reduce((messages, key) => {
      const value = nestedMessages[key];
      const prefixedKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === "string") {
        messages[prefixedKey] = value; // eslint-disable-line no-param-reassign
      } else {
        Object.assign(messages, this.flattenMessages(value, prefixedKey));
      }

      return messages;
    }, {});

  getDateLocale = () => this.LOCALE;

  getMessagesFromLocale = (locale) =>
    this.isLocaleSupported(locale)
      ? get(this.localeData, `[${locale}].messages`)
      : get(this.localeData, `[${this.DEFAULT_LOCALE}].messages`);

  getIntlConfigFromNavigator = (locale = this.LOCALE) =>
    this.getIntlConfigFromLocale(locale);

  getIntlConfigFromLocale = (localeFromArgs = this.LOCALE) => {
    // The API provide non-ISO locale with '_' instead of using '-'
    // rfc5646: https://tools.ietf.org/html/rfc5646
    // ex: en_gb is provided by the API instead of en-gb
    const locale = localeFromArgs.replace("_", "-");

    return {
      locale,
      messages: this.getMessagesFromLocale(locale),
    };
  };

  getIntlInstance = (): { messages } => {
    if (this.intlInstance) {
      return this.intlInstance;
    }

    const intlCache = createIntlCache();
    const intlConfig = this.getIntlConfigFromLocale();
    this.intlInstance = createIntl(intlConfig, intlCache);

    return this.intlInstance;
  };

  isLocaleSupported = (locale) => this.LOCALES.indexOf(locale) >= 0;

  setLocale = ({ locale = this.DEFAULT_LOCALE, timezone = "Europe/Paris" }) => {
    const normalizedLocale = this.isLocaleSupported(locale)
      ? locale
      : this.DEFAULT_LOCALE;

    this.LOCALE = normalizedLocale;
    moment.locale(normalizedLocale);
    moment.tz.setDefault(timezone);
  };
}

export default new IntlUtils();
